import ActionableMessageCardStrings from '../store/schema/ActionableMessageCardStrings';
import {
    setProperty,
    getBoolValue,
    getStringValue,
    Input,
    Choice,
    ValidationResults,
    ValidationError,
    IValidationError,
} from 'adaptivecards';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { ChoiceGroup } from '@fluentui/react/lib/ChoiceGroup';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { trace } from 'owa-trace';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

interface MappedChoice {
    key: string;
    text: string;
    index: number;
}

export default class CustomChoiceSetInput extends Input {
    private element: HTMLDivElement;
    private selectedChoiceIndices: Array<number>;
    private mappedChoices: MappedChoice[];
    private initialized: boolean;

    public static strings: ActionableMessageCardStrings;

    public choices: Array<Choice> = [];
    public isCompact: boolean = false;
    public isMultiSelect: boolean = false;
    public placeholder: string;
    public wrap: boolean = false;
    public isRequired: boolean = false;

    private renderCheckboxGroup(defaultValues: string[]): JSX.Element {
        const checkboxElementArray = [];
        for (let i = 0; i < this.choices.length; i++) {
            checkboxElementArray.push(
                <Checkbox
                    defaultChecked={
                        defaultValues.filter(v => v === this.choices[i].value).length > 0
                    }
                    label={this.choices[i].title}
                    onChange={this.getOnCheckboxChangedHandler(i)}
                />
            );
        }

        return <>{checkboxElementArray}</>;
    }

    private onDropdownChanged = (option: IDropdownOption, index?: number) => {
        this.selectedChoiceIndices = [index];
    };

    private onChoiceGroupChanged = (
        ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        option?: MappedChoice
    ) => {
        this.selectedChoiceIndices = [option.index];
    };

    private getOnCheckboxChangedHandler(
        index: number
    ): (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void {
        return (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
            if (checked) {
                this.selectedChoiceIndices.push(index);
            } else {
                this.selectedChoiceIndices.splice(this.selectedChoiceIndices.indexOf(index), 1);
            }
        };
    }

    private parseValueString(valueString: string): string[] {
        if (valueString) {
            return valueString.split(this.hostConfig.choiceSetInputValueSeparator);
        }

        return [];
    }

    protected internalRender(): HTMLElement {
        this.element = document.createElement('div');
        this.element.style.width = '100%';
        if (!this.initialized) {
            trace.warn(
                '[ActionableMessageDebug] CustomChoiceSetInput: Control not initialized before render'
            );
            return this.element;
        }

        if (!this.isMultiSelect) {
            if (this.isCompact) {
                const placeholderValue =
                    this.placeholder || CustomChoiceSetInput.strings.placeholder.choiceSetInput;
                ReactDOM.render(
                    <React.StrictMode>
                        <Dropdown
                            onChanged={this.onDropdownChanged}
                            label={this.title}
                            options={this.mappedChoices}
                            defaultSelectedKey={this.defaultValue}
                            placeHolder={placeholderValue}
                            required={this.isRequired}
                        />
                    </React.StrictMode>,
                    this.element
                );
            } else {
                ReactDOM.render(
                    <React.StrictMode>
                        <ChoiceGroup
                            onChange={this.onChoiceGroupChanged}
                            label={this.title}
                            options={this.mappedChoices}
                            defaultSelectedKey={this.defaultValue}
                            required={this.isRequired}
                        />
                    </React.StrictMode>,
                    this.element
                );
            }
        } else {
            ReactDOM.render(
                this.renderCheckboxGroup(this.parseValueString(this.defaultValue)),
                this.element
            );
        }

        return this.element;
    }

    public initialize(): void {
        // Map the choices from Adaptive.Choice to options compatible with the fabric controls
        this.mappedChoices = this.choices.map((b: Choice, index: number) => {
            return { key: b.value, text: b.title, index: index };
        });

        this.selectedChoiceIndices = [];
        const defaultValues = this.parseValueString(this.defaultValue);
        for (let i = 0; i < this.choices.length; i++) {
            if (defaultValues.filter(v => v === this.choices[i].value).length > 0) {
                this.selectedChoiceIndices.push(i);
            }
        }

        this.initialized = true;
    }

    public toJSON(): any {
        let result = super.toJSON();

        if (this.choices.length > 0) {
            var choices = [];

            for (let choice of this.choices) {
                choices.push(choice.toJSON());
            }

            setProperty(result, 'choices', choices);
        }

        if (!this.isCompact) {
            setProperty(result, 'style', 'expanded', false);
        }

        setProperty(result, 'isMultiSelect', this.isMultiSelect, false);
        setProperty(result, 'wrap', this.wrap, false);
        setProperty(result, 'placeholder', this.placeholder);
        setProperty(result, 'isRequired', this.isRequired, false);

        return result;
    }

    public parse(json: any, errors?: Array<IValidationError>) {
        super.parse(json, errors);

        this.isCompact = !(json['style'] === 'expanded');
        this.isMultiSelect = getBoolValue(json['isMultiSelect'], this.isMultiSelect);
        this.placeholder = getStringValue(json['placeholder'], this.placeholder);

        this.choices = [];

        if (json['choices'] != undefined) {
            let choiceArray = json['choices'] as Array<any>;

            for (let i = 0; i < choiceArray.length; i++) {
                let choice = new Choice();
                choice.parse(choiceArray[i]);

                this.choices.push(choice);
            }
        }

        this.wrap = getBoolValue(json['wrap'], this.wrap);
        this.isRequired = getBoolValue(json['isRequired'], this.isRequired);

        this.initialize();
    }

    public remove(): boolean {
        if (this.element) {
            ReactDOM.unmountComponentAtNode(this.element);
            this.element = null;
        }

        return super.remove();
    }

    public internalValidateProperties(context: ValidationResults) {
        if (this.isRequired && !this.value) {
            context.addFailure(this, {
                error: ValidationError.InvalidPropertyValue,
                message: CustomChoiceSetInput.strings.warning.missingRequiredInput,
            });
        }
    }

    public getJsonTypeName(): string {
        return 'Input.ChoiceSet';
    }

    get value(): string {
        let result = '';
        if (this.selectedChoiceIndices) {
            for (let i = 0; i < this.selectedChoiceIndices.length; i++) {
                if (result !== '') {
                    result += this.hostConfig.choiceSetInputValueSeparator;
                }

                const choiceIndex = this.selectedChoiceIndices[i];
                if (this.choices[choiceIndex].value) {
                    result += this.choices[choiceIndex].value;
                } else {
                    result += this.choices[choiceIndex].title;
                }
            }
        }

        return result;
    }
}
